/**
 * The reseller's payout method (H §6.3, `GET /payout-method`): method, tax-docs status and the
 * Stripe Connect state. OWNER / ADMIN may **Set up Stripe Connect** (no account yet) or
 * **Continue onboarding** (an unfinished account → `POST …/onboarding-link`); the returned
 * `onboardingUrl` opens in the same tab only when it is an https Stripe URL. STAFF sees the
 * state only.
 */
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { PayoutMethodView } from '@/api/generated';
import { resellerAdminApi } from '@/api/resellerAdmin';
import { useCanWrite } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useRemote } from '@/components/useRemote';
import { StatusChip } from '@/components/status/StatusChip';
import { enumLabel } from '@/i18n/enumLabel';
import { NotPricedState } from '@/tenant/NotPricedState';
import { isPayoutMethodNotConfigured } from './notConfigured';
import { isStripeOnboardingUrl, navigateTo } from './onboardingUrl';
import { StripeConnectDialog } from './StripeConnectDialog';

/** Statuses whose onboarding is unfinished — the member can pick it up again. */
const RESUMABLE = new Set(['CREATING', 'ONBOARDING', 'RESTRICTED']);

const loadPayoutMethod = () => resellerAdminApi.getPayoutMethod();

export function PayoutMethodPanel() {
  const { t } = useTranslation();
  const location = useLocation();
  const { status, data, error, reload } = useRemote(loadPayoutMethod);
  const revealed = useRef(false);
  // `#payout-method` does not scroll on its own. Once the read has settled, scroll the panel
  // and focus its heading — not whatever heading happens to be first.
  useEffect(() => {
    if (revealed.current) return;
    if (location.hash !== '#payout-method') return;
    if (status === 'loading' && data === null) return;
    const panel = document.getElementById('payout-method');
    const heading = document.getElementById('payout-method-heading');
    if (!(panel instanceof HTMLElement) || !(heading instanceof HTMLElement)) return;
    revealed.current = true;
    panel.scrollIntoView();
    heading.focus({ preventScroll: true });
  }, [location.hash, status, data]);

  return (
    <Paper id="payout-method" sx={{ p: 2.5 }}>
      <Typography
        id="payout-method-heading"
        variant="subtitle1"
        component="h2"
        tabIndex={-1}
        sx={{ mb: 1.5, '&:focus:not(:focus-visible)': { outline: 'none' } }}
      >
        {t('payouts.method.title')}
      </Typography>
      {status === 'error' && isPayoutMethodNotConfigured(error) ? <NotPricedState /> : null}
      {status === 'error' && !isPayoutMethodNotConfigured(error) ? (
        <ApiErrorState error={error} onRetry={reload} />
      ) : null}
      {status === 'loading' && !data ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={96} />
        </Box>
      ) : null}
      {status !== 'error' && data ? (
        <PayoutMethodReady method={data} onCreated={reload} />
      ) : null}
    </Paper>
  );
}

function PayoutMethodReady({
  method,
  onCreated,
}: {
  method: PayoutMethodView;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const canWrite = useCanWrite();
  const describe = useActionError();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const connect = method.stripeConnect;
  const connectStatus = connect?.status;
  const available = method.connectAvailability?.available !== false;
  const requirements = connect?.requirementsDue?.length ?? 0;

  async function handleContinue() {
    setError(null);
    setBusy(true);
    try {
      const view = await resellerAdminApi.refreshStripeConnectLink();
      if (isStripeOnboardingUrl(view.onboardingUrl)) {
        navigateTo(view.onboardingUrl);
        return;
      }
      setError({
        message: t('payouts.connect.unsafeUrl'),
        fieldKeys: [],
        code: undefined,
        requestId: undefined,
      });
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={1.5}>
      <Box
        component="dl"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 2,
          rowGap: 1,
          m: 0,
        }}
      >
        <Typography component="dt" variant="body2" sx={{ color: 'text.secondary' }}>
          {t('payouts.method.method')}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0 }}>
          {method.method
            ? enumLabel(t, 'statements.method', method.method)
            : t('statements.unavailable')}
        </Typography>
        <Typography component="dt" variant="body2" sx={{ color: 'text.secondary' }}>
          {t('payouts.method.taxDocs')}
        </Typography>
        <Typography component="dd" variant="body2" sx={{ m: 0 }}>
          {method.taxDocsStatus
            ? enumLabel(t, 'statements.tax', method.taxDocsStatus)
            : t('statements.unavailable')}
        </Typography>
        <Typography component="dt" variant="body2" sx={{ color: 'text.secondary' }}>
          {t('payouts.method.connect')}
        </Typography>
        <Box
          component="dd"
          sx={{ m: 0, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
        >
          {connectStatus ? (
            <StatusChip domain="connect" status={connectStatus} />
          ) : (
            <Typography variant="body2">{t('payouts.connect.notSetUp')}</Typography>
          )}
          {connect && connect.payoutsEnabled === true ? (
            <Typography variant="caption">{t('payouts.connect.payoutsEnabled')}</Typography>
          ) : null}
          {requirements > 0 ? (
            <Typography variant="caption" sx={{ color: 'warning.main' }}>
              {t('payouts.connect.requirementsDue', { count: requirements })}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {!available ? <Alert severity="info">{t('payouts.connect.unavailable')}</Alert> : null}
      <ActionErrorAlert error={error} />
      {canWrite && available && !connect ? (
        <Box>
          <Button variant="contained" onClick={() => setDialogOpen(true)}>
            {t('payouts.connect.setUp')}
          </Button>
        </Box>
      ) : null}
      {canWrite && connectStatus !== undefined && RESUMABLE.has(connectStatus) ? (
        <Box>
          <Button variant="contained" onClick={() => void handleContinue()} disabled={busy}>
            {t('payouts.connect.continue')}
          </Button>
        </Box>
      ) : null}
      <StripeConnectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={onCreated}
      />
    </Stack>
  );
}
